import { Module } from "@/constants";

export interface ParsedRequestUrl {
  endpoint: string;
  itemId: string | null;
  moduleKey: string;
}

/**
 * Parses API request URL to extract endpoint, item ID, and module key.
 *
 * @param url - The full API request URL
 * @returns Object containing endpoint, itemId, and moduleKey
 *
 * @example
 * parseRequestUrl("https://api.dev.redcurrant.eu/v1/networks/123")
 * // Returns: { endpoint: "networks", itemId: "123", moduleKey: "networks" }
 *
 * parseRequestUrl("https://api.dev.redcurrant.eu/v1/users")
 * // Returns: { endpoint: "users", itemId: null, moduleKey: "users" }
 */
export function parseRequestUrl(url: string): ParsedRequestUrl {
  const urlPath = new URL(url).pathname;
  const pathParts = urlPath.split("/").filter(Boolean);

  if (pathParts.length < 2) {
    throw new Error(`Invalid API URL format: ${url}`);
  }

  let endpoint = "";
  let itemId: string | null = null;

  // If the last part is a number/ID, use the second-to-last part as the endpoint
  if (pathParts.length > 2 && !isNaN(Number(pathParts[pathParts.length - 1]))) {
    endpoint = pathParts[pathParts.length - 2];
    itemId = pathParts[pathParts.length - 1];
  } else {
    // Otherwise, use the last part as the endpoint
    endpoint = pathParts[pathParts.length - 1];
  }

  // Map API endpoint to Module enum value
  const moduleKey =
    Object.values(Module).find((module) => module === endpoint) || endpoint;

  return {
    endpoint,
    itemId,
    moduleKey,
  };
}
